import { canBeAColumnChild } from '../../../../common/columns/columnUtils';
import {
    getDrawingSvg,
    getIsLineEndSnapped,
    getIsLineStartSnapped,
    getIsTransparentImage,
    getLocationParentId,
    getMediaType,
    getShowBackground,
    getShowFullAspectRatio,
    isElementLocked,
} from '../../../../common/elements/utils/elementPropertyUtils';
import {
    canBeCloned,
    isAlias,
    isAnnotation,
    isBoard,
    isBoardLike,
    isCard,
    isColumn,
    isContainer,
    isDocument,
    isDocumentLike,
    isDrawing,
    isFile,
    isImage,
    isLine,
    isLink,
} from '../../../../common/elements/utils/elementTypeUtils';
import { isLocationCanvas, isLocationInbox } from '../../../../common/elements/utils/elementLocationUtils';
import { MEDIA_TYPES } from '../../../../common/links/richMediaConstants';
import { BrowserEngineType } from '../../../../common/platform/platformTypes';
import { getIsTemplate } from '../../../../common/templates/templateUtil';
import { isPlatformApple, isPlatformNativeApp } from '../../../platform/utils/platformDetailsUtils';
import { isVideoFile } from '../../../element/file/utils/fileGetMediaPlayerType';
import { getPasteElementStyleUpdates } from '../../shortcuts/clipboard/clipboardStyleUtils';
import { isMediaImageShowing } from '../../../../common/elements/utils/elementMediaUtil';
import type { ElementContextMenuBaseContext } from '../context/elementContextMenuContext';

const hasSelection = (context: ElementContextMenuBaseContext): boolean => context.selectedElements.size > 0;
const isSingle = (context: ElementContextMenuBaseContext): boolean => context.selectedElements.size === 1;

const isSingleBoard = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every(isBoardLike);
const isSingleLink = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every(isLink);
const isSingleFile = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every(isFile);
const isSingleImage = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every(isImage);
const areAllImages = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) && context.selectedElements.every(isImage);
const areAllFilesOrImages = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) && context.selectedElements.every((element) => isFile(element) || isImage(element));
const isSinglePopulatedDrawing = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every((element) => isDrawing(element) && !!getDrawingSvg(element));
const canPaste = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) && context.selectedElements.every(isColumn) && context.clipboard.elements.size > 0;
const canPasteStyles = (context: ElementContextMenuBaseContext): boolean => {
    const sourceElement = context.clipboard.elements.first()?.toJS();
    if (!sourceElement) return false;

    return (
        getPasteElementStyleUpdates({
            sourceElement,
            targetElements: context.selectedElements.toArray(),
        }).length > 0
    );
};
const canRename = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every((element) => isContainer(element) || isDocumentLike(element));
const canReplaceImage = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) &&
    context.selectedElements.every(
        (element) => isImage(element) || isVideoFile(element) || isMediaImageShowing(element),
    );
const canAddImage = (context: ElementContextMenuBaseContext): boolean => isSingleLink(context) && !canReplaceImage(context);
const canToggleImageAspectRatio = (context: ElementContextMenuBaseContext): boolean =>
    areAllImages(context) &&
    !context.selectedElements.every((element) => !getShowBackground(element) && getIsTransparentImage(element));
const canCopyLinkAddress = (context: ElementContextMenuBaseContext): boolean =>
    isSingleLink(context) &&
    context.clipboardInsertSupported &&
    context.platformDetails?.browserEngine !== BrowserEngineType.firefox;
const canGroupIntoColumn = (context: ElementContextMenuBaseContext): boolean =>
    context.selectedElements.size > 1 &&
    context.selectedElements.every(
        (element) => !!element && getLocationParentId(element) === context.currentBoardId && canBeAColumnChild(element),
    );
const canConnectWithLines = (context: ElementContextMenuBaseContext): boolean =>
    context.selectedElements.filter((element) => !isLine(element) && isLocationCanvas(element)).size > 1;
const canConvertToSketch = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every(isAnnotation);
const canConvertTemplate = (context: ElementContextMenuBaseContext): boolean =>
    context.selectedElements.filter(isBoard).size === 1;
const canDuplicateAliasToBoard = (context: ElementContextMenuBaseContext): boolean =>
    context.selectedElements.filter(isAlias).size === 1;
const canClone = (context: ElementContextMenuBaseContext): boolean =>
    isSingle(context) && context.selectedElements.every(canBeCloned);
const willEnableLock = (context: ElementContextMenuBaseContext): boolean =>
    !context.selectedElements.some((element) => !!element && isElementLocked(element));
const canBringToFront = (context: ElementContextMenuBaseContext): boolean =>
    !context.selectedElements.some(isLocationInbox);
const canSendToBack = (context: ElementContextMenuBaseContext): boolean => {
    const firstElement = context.selectedElements.first();
    if (!firstElement) return false;
    if (!isLine(firstElement)) return !context.selectedElements.some(isLocationInbox);

    return !getIsLineStartSnapped(firstElement) && !getIsLineEndSnapped(firstElement);
};
const canConvertToNote = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) && context.selectedElements.every(isDocument);
const canConvertToDocument = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) && context.selectedElements.every(isCard);
const canConvertToBoard = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) && context.selectedElements.every(isColumn);
const isApplePlatform = (context: ElementContextMenuBaseContext): boolean =>
    !!context.platformDetails && isPlatformApple(context.platformDetails);
const isImageCroppedToGrid = (context: ElementContextMenuBaseContext): boolean =>
    !getShowFullAspectRatio(context.selectedElements.first());
const isSelectedBoardTemplate = (context: ElementContextMenuBaseContext): boolean =>
    !!getIsTemplate(context.selectedElements.filter(isBoard).first());
const isVideoSelection = (context: ElementContextMenuBaseContext): boolean =>
    hasSelection(context) &&
    context.selectedElements.every((element) => isVideoFile(element) || getMediaType(element) === MEDIA_TYPES.VIDEO);
const supportsOpenInNewTab = (context: ElementContextMenuBaseContext): boolean =>
    isSingleBoard(context) && (!context.platformDetails || !isPlatformNativeApp(context.platformDetails));

export const ElementContextMenuPredicates = {
    areAllFilesOrImages,
    areAllImages,
    canAddImage,
    canBringToFront,
    canClone,
    canConnectWithLines,
    canConvertTemplate,
    canConvertToBoard,
    canConvertToDocument,
    canConvertToNote,
    canConvertToSketch,
    canCopyLinkAddress,
    canDuplicateAliasToBoard,
    canGroupIntoColumn,
    canPaste,
    canPasteStyles,
    canRename,
    canReplaceImage,
    canSendToBack,
    canToggleImageAspectRatio,
    isSingleBoard,
    isSingleFile,
    isSingleImage,
    isSingleLink,
    isSinglePopulatedDrawing,
    isApplePlatform,
    isImageCroppedToGrid,
    isSelectedBoardTemplate,
    isVideoSelection,
    supportsOpenInNewTab,
    willEnableLock,
};
